import { debugFlag } from '@/app/debugFlags/debugFlags';
import { authClient } from '@/auth/auth';
import { apiClient } from '@/shared/api/apiClient';
import { requestRefreshIfClientStale } from '@/shared/utils/requestRefreshIfClientStale';
import { captureException } from '@sentry/react';
import type z from 'zod';

export class ApiError extends Error {
  status: number;
  details?: string;
  method?: string;

  constructor(message: string, status: number, method?: string, details?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status; // Fetch response status code
    this.method = method; // Fetch request method
    this.details = details; // Details useful for debugging
  }
}

/**
 * Error thrown when the schema configuration is missing or invalid.
 * This indicates a programming error (e.g., missing schema in ApiSchemas),
 * not a runtime API failure.
 */
export class SchemaConfigurationError extends TypeError {
  path: string;
  method?: string;

  constructor(message: string, path: string, method?: string) {
    super(message);
    this.name = 'SchemaConfigurationError';
    this.path = path;
    this.method = method;
  }
}

type FetchFromApiInit = RequestInit & {
  skipAuthRedirect?: boolean;
};

export async function fetchFromApi<T>(
  path: string,
  init: FetchFromApiInit,
  schema: z.Schema<T>
): Promise<z.infer<typeof schema>> {
  const { skipAuthRedirect = false, ...requestInit } = init;
  // Validate that schema is defined and has safeParse method before proceeding
  if (!schema || typeof schema.safeParse !== 'function') {
    const schemaType = schema === undefined ? 'undefined' : schema === null ? 'null' : typeof schema;
    const errorMessage =
      `Schema configuration error for API call: ${requestInit.method} ${path}. ` +
      `Expected a Zod schema but received ${schemaType}. ` +
      `This likely indicates a missing or incorrect schema definition in ApiSchemas.`;
    console.error(errorMessage, { path, method: requestInit.method, schemaType });
    const error = new SchemaConfigurationError(errorMessage, path, requestInit.method);
    captureException(error);
    throw error;
  }

  const debugTime = `API ${requestInit.method} ${path}`;
  if (debugFlag('debugShowAPITimes')) console.time(debugTime);

  // We'll automatically inject additional headers to the request, starting with auth
  const isAuthenticated = await authClient.isAuthenticated();
  const skipRedirect = skipAuthRedirect || window.location.pathname.includes('/login-result');
  const token = isAuthenticated ? await authClient.getTokenOrRedirect(skipRedirect) : '';
  const headers = new Headers(requestInit.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // And if we're submitting `FormData`, let the browser set the content-type automatically
  // This allows files to upload properly. Otherwise, we assume it's JSON.
  if (!(requestInit.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  // And finally, we'll set the headers back on the request
  requestInit.headers = headers;

  // Make API call
  const url = apiClient.getApiUrl() + path;
  const response = await fetch(url, requestInit);

  // Handle if the response is not JSON
  const json = await response.json().catch(async () => {
    // A non-JSON body where JSON is expected almost always means a stale client
    // received an HTML page (the SPA shell or a gateway/error page) for an
    // endpoint whose contract has since changed — e.g. "Unexpected token '<',
    // "<!DOCTYPE "... is not valid JSON". Prompt a refresh and throw a
    // contextful ApiError (with the URL) instead of capturing a location-less
    // SyntaxError, matching the schema-mismatch branch below.
    const staleness = await requestRefreshIfClientStale();
    // If the client is confirmed current, this isn't deploy skew — it's a
    // genuine server/gateway bug (e.g. HTML returned for a 200), so preserve
    // observability with a contextful capture.
    if (staleness === 'current') {
      captureException(new Error(`Non-JSON API response from ${url}`), {
        extra: { status: response.status, method: requestInit.method },
      });
    }
    throw new ApiError('Response is not JSON', response.status, requestInit.method, url);
  });

  // Handle response if a server error is returned
  if (!response.ok) {
    // TODO: ensure API only ever returns uniform error response, e.g. `json.errors` or `json.error.message`
    let details = 'No detailed error message provided';
    if (json?.error?.message) {
      details = json.error.message;
    } else if (json?.errors) {
      details = JSON.stringify(json.errors);
    }
    throw new ApiError(`Failed to fetch ${url}`, response.status, requestInit.method, details);
  }

  // Compare the response to the expected schema
  const result = schema.safeParse(json);

  if (!result.success) {
    console.error(`Zod schema validation failed at: ${path}`, JSON.stringify(result.error, null, 2));

    // A successful response that fails our schema usually means this client
    // bundle is stale relative to the deployed API; prompt a (required) refresh.
    void requestRefreshIfClientStale();

    const details = JSON.stringify(result.error);
    throw new ApiError('Unexpected response schema', response.status, requestInit.method, details);
  }

  if (debugFlag('debugShowAPITimes')) console.timeEnd(debugTime);
  return result.data;
}
